import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ExternalLink, Sparkles, Zap } from "lucide-react";

import { openExternal } from "@/lib/api";
import { AddRoutineButton, CartIconButton, firstAvailable, LiveBuyBox, LiveImage, PriceTag, StockBadge } from "@/components/shopui";
import { Button, Card, Spinner, Tag } from "@/components/kit";
import { Page } from "@/components/layout";
import { useShop } from "@/shop";
import { useApp } from "@/store";

function ProductCard({ p, label }) {
  const navigate = useNavigate();
  const v = p.shopify ? firstAvailable(p.shopify) : undefined;
  return (
    <button type="button" data-testid={`shop-product-${p.slug}`} onClick={() => navigate(`/product/${p.slug}`)} className={`focus-ring flex flex-col rounded-[20px] bg-white p-2 pb-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-card ${label ? "border-2 border-ocean" : "border border-line"}`}>
      <LiveImage live={p.shopify} name={p.name} height={150} />
      {label ? <span className="mt-2"><Tag tone="navy" label={label} /></span> : null}
      <span className="t-title mt-2 line-clamp-2">{p.shopify?.title ?? p.name}</span>
      <span className="mt-1 flex flex-col gap-1">
        <PriceTag variant={v} testId={`shop-price-${p.slug}`} size="sm" />
        <StockBadge variant={v} testId={`shop-stock-${p.slug}`} />
      </span>
    </button>
  );
}

export default function Shop() {
  const { hasProfile, routine: storeRoutine } = useApp();
  const { catalog, catalogError, loadCatalog, refreshCart } = useShop();
  const navigate = useNavigate();
  const routine = hasProfile ? storeRoutine : null;
  useEffect(() => {
    loadCatalog();
    refreshCart();
  }, [loadCatalog, refreshCart]);

  const products = catalog?.products ?? null;
  const picks = routine ? [routine.primary, routine.supporting].filter(Boolean) : [];
  const pickSlugs = picks.map((p) => p.slug);
  const offline = catalog ? !catalog.live : !!catalogError;

  return (
    <div data-testid="shop-screen">
      <Page wide className="flex items-end gap-3 border-b border-divider pb-4 pt-6">
        <div className="flex-1">
          <p className="t-label">BREEZ® · breez.au</p>
          <h1 data-testid="shop-title" className="t-h1">SHOP YOUR ROUTINE.</h1>
        </div>
        <CartIconButton testId="shop-cart-button" />
      </Page>
      <Page wide className="flex flex-col gap-5 py-6">
        {offline ? (
          <Card testId="shop-offline-banner" className="!border-[#F59E0B]">
            <p className="t-strong">Live prices unavailable — view on breez.au.</p>
            <div className="mt-3 flex max-w-md gap-3">
              <Button testId="shop-retry" title="Retry" variant="outline" onClick={() => loadCatalog(true)} className="flex-1" />
              <Button testId="shop-open-breez" title="breez.au" icon={ExternalLink} onClick={() => openExternal("https://breez.au")} className="flex-1" />
            </div>
          </Card>
        ) : catalog ? (
          <div data-testid="shop-live-banner" className="flex items-center gap-2 rounded-xl bg-sky p-3">
            <Zap className="h-4 w-4 text-navy" aria-hidden />
            <p className="t-caption flex-1 !text-navy">Live prices &amp; stock from breez.au · {catalog.currency}</p>
          </div>
        ) : null}

        {routine ? (
          <section data-testid="shop-routine" className="flex flex-col gap-3">
            <h2 className="t-h2">Picked for your hair</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {picks.map((pk, i) => {
                const p = products?.find((x) => x.slug === pk.slug);
                return (
                  <Card key={pk.slug} testId={`shop-pick-${pk.slug}`} className="flex flex-col gap-3">
                    <div className="flex gap-3">
                      <button type="button" data-testid={`shop-pick-open-${pk.slug}`} onClick={() => navigate(`/product/${pk.slug}`)} className="focus-ring w-24 shrink-0 rounded-xl"><LiveImage live={p?.shopify} name={pk.name} height={96} /></button>
                      <div className="flex flex-1 flex-col gap-1">
                        <Tag tone={i === 0 ? "navy" : "sky"} label={i === 0 ? "Your match" : "Supporting"} />
                        <p className="t-title line-clamp-2">{p?.shopify?.title ?? pk.name}</p>
                        {pk.reason ? <p data-testid={`shop-highlight-reason-${pk.slug}`} className="t-caption line-clamp-4">{pk.reason}</p> : null}
                      </div>
                    </div>
                    <LiveBuyBox slug={pk.slug} testPrefix={`shop-pick-${pk.slug}`} />
                  </Card>
                );
              })}
            </div>
            <div className="md:max-w-sm"><AddRoutineButton slugs={pickSlugs} testId="shop-add-routine" /></div>
          </section>
        ) : (
          <button type="button" data-testid="shop-get-match" onClick={() => navigate("/scan")} className="focus-ring flex items-center gap-3 rounded-[20px] border border-line bg-white p-4 text-left hover:border-ocean">
            <Sparkles className="h-5 w-5 text-navy" aria-hidden />
            <span className="t-strong flex-1">Scan or add your hair details to get your personal product match.</span>
            <ChevronRight className="h-[18px] w-[18px] text-mute" aria-hidden />
          </button>
        )}

        <h2 className="t-h2">All products</h2>
        {!products && !offline ? <Spinner /> : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {(products ?? []).map((p) => <ProductCard key={p.slug} p={p} label={pickSlugs[0] === p.slug ? "Your match" : undefined} />)}
          </div>
        )}

        {catalog?.kits?.length ? (
          <section data-testid="shop-kits" className="flex flex-col gap-3">
            <h2 className="t-h2">Kits</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {catalog.kits.map((k) => {
                const v = firstAvailable(k);
                return (
                  <button key={k.handle} type="button" data-testid={`shop-kit-${k.handle}`} onClick={() => navigate(`/product/kit-${k.handle}`)} className="focus-ring flex items-center gap-3 rounded-[20px] border border-line bg-white p-2 pr-3 text-left transition-all hover:border-ocean">
                    <span className="w-[84px] shrink-0"><LiveImage live={k} name={k.title} height={84} /></span>
                    <span className="flex flex-1 flex-col gap-1">
                      <span className="line-clamp-2 font-display-semi text-base text-ink">{k.title}</span>
                      <PriceTag variant={v} size="sm" testId={`shop-kit-price-${k.handle}`} />
                      <StockBadge variant={v} testId={`shop-kit-stock-${k.handle}`} />
                    </span>
                    <ChevronRight className="h-[18px] w-[18px] text-mute" aria-hidden />
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}
        <p className="t-caption text-center">Checkout happens securely on breez.au (Shopify). BREEZ AI never takes payment details.</p>
      </Page>
    </div>
  );
}
